import React, { useCallback, useRef, useState } from 'react'
import { nanoid } from '@reduxjs/toolkit'
import classNames from 'classnames'

import { useIsVisible } from '../../hooks/useIsVisible'

import { ScheduleEntry } from './types'
import ScheduleBlock from './ScheduleBlock'
import { DEFAULT_DURATION_MINUTES, PIXELS_PER_MINUTE, SNAP_INCREMENT_MINUTES } from './constants'

const SECONDS_IN_A_DAY = 86400

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

interface SchedulerProps {
    maxCurrent: number // The current (amps) value
    disabled?: boolean
    isActive?: boolean
    customMessage?: string
    customStyle?: React.CSSProperties
    schedules: ScheduleEntry[]
    onSchedulesChange: (schedules: ScheduleEntry[]) => void
    onOpenActions: (event: React.MouseEvent, schedule: ScheduleEntry) => void
    actionsLabel: string
    getWarningMessage?: (schedulePeriod: ScheduleEntry) => string | null
    getCustomStyle?: (schedulePeriod: ScheduleEntry) => React.CSSProperties | undefined
    getCustomMessage?: () => string
    formatCurrent?: (maxCurrent: number) => string
}

export const Scheduler: React.FC<SchedulerProps> = ({
    maxCurrent,
    disabled = false,
    isActive,
    schedules,
    onSchedulesChange,
    onOpenActions,
    actionsLabel,
    getWarningMessage,
    getCustomStyle,
    getCustomMessage,
    formatCurrent,
}) => {
    const [isDragging, setIsDragging] = useState(false)

    const handleSlotClick = useCallback(
        (e: React.MouseEvent, dayIndex: number) => {
            // Ignore the click if it originated not from the time slot
            if (!(e.target as HTMLElement).classList.contains('time-slot')) {
                return
            }

            if (isDragging || disabled) {
                e.stopPropagation()
                return
            }

            const rect = e.currentTarget.getBoundingClientRect()
            const y = e.clientY - rect.top
            const minutesFromMidnight = y / PIXELS_PER_MINUTE

            // round minutes from the midnight to the hour
            let from = dayIndex * SECONDS_IN_A_DAY + (minutesFromMidnight - (minutesFromMidnight % 60)) * 60

            from = snapToGrid(from)
            const to = from + DEFAULT_DURATION_MINUTES * 60

            const newSchedule: ScheduleEntry = {
                id: nanoid(),
                from,
                to,
                maxCurrent,
            }

            const updatedSchedules = mergeSchedules([...schedules, newSchedule])
            onSchedulesChange(updatedSchedules)
        },
        [maxCurrent, disabled, isDragging, onSchedulesChange, schedules]
    )

    const handleScheduleUpdate = useCallback(
        (updatedSchedule: ScheduleEntry) => {
            const updatedSchedules = mergeSchedules(
                schedules.map((schedule) => (schedule.id === updatedSchedule.id ? updatedSchedule : schedule))
            )
            onSchedulesChange(updatedSchedules)
        },
        [onSchedulesChange, schedules]
    )
    const onDragStart = useCallback(() => {
        setIsDragging(true)
    }, [])

    const onDragEnd = useCallback(() => {
        setIsDragging(false)
    }, [])

    const snapToGrid = (seconds: number): number => {
        const minutes = seconds / 60
        const snappedMinutes = Math.round(minutes / SNAP_INCREMENT_MINUTES) * SNAP_INCREMENT_MINUTES
        return snappedMinutes * 60 // Convert back to seconds
    }

    const containerRef = useRef<HTMLDivElement>(null)
    const isVisible = useIsVisible(containerRef)

    return (
        <div className="scheduler" ref={containerRef}>
            <div className={classNames('calendar-grid', { 'is-active': isActive })}>
                {/* Time labels */}
                <div className="time-labels">
                    {[...Array(24)].map((_, hour) => (
                        <div key={hour} className="time-label">
                            {`${hour.toString().padStart(2, '0')}:00`}
                        </div>
                    ))}
                </div>
                {/* Needs to be visible to calculate the correct top position of the schedule */}
                {isVisible &&
                    daysOfWeek.map((day, dayIndex) => (
                        <div key={day} className="day-column">
                            <div className="day-header">{day}</div>
                            <div
                                className={classNames('time-slots', { disabled })}
                                onClick={(e) => handleSlotClick(e, dayIndex)}
                            >
                                <div className="time-slots-inner">
                                    {[...Array(24)].map((_, hour) => (
                                        <div key={hour} className="time-slot"></div>
                                    ))}
                                </div>
                                {/* Schedule entries */}
                                {schedules
                                    .filter((schedule) => {
                                        const scheduleStartDay = Math.floor(schedule.from / SECONDS_IN_A_DAY)
                                        return (
                                            scheduleStartDay === dayIndex ||
                                            (schedule.from < (dayIndex + 1) * SECONDS_IN_A_DAY &&
                                                schedule.to > dayIndex * SECONDS_IN_A_DAY)
                                        )
                                    })
                                    .map((schedule) => (
                                        <ScheduleBlock
                                            key={`${schedule.id}-${dayIndex}`}
                                            schedule={schedule}
                                            onUpdate={handleScheduleUpdate}
                                            onDragStart={onDragStart}
                                            onDragEnd={onDragEnd}
                                            dayIndex={dayIndex}
                                            schedules={schedules}
                                            disabled={disabled}
                                            onOpenActions={(event) => onOpenActions(event, schedule)}
                                            actionsLabel={actionsLabel}
                                            warning={getWarningMessage?.(schedule)}
                                            customStyle={getCustomStyle?.(schedule)}
                                            customMessage={getCustomMessage?.()}
                                            formatCurrent={formatCurrent}
                                        />
                                    ))}
                            </div>
                        </div>
                    ))}
            </div>
        </div>
    )
}

const mergeSchedules = (scheduleList: ScheduleEntry[]): ScheduleEntry[] => {
    const sortedSchedules = scheduleList.sort((a, b) => a.from - b.from)
    const merged: ScheduleEntry[] = []

    for (const schedule of sortedSchedules) {
        const last = merged[merged.length - 1]
        if (
            last &&
            last.to >= schedule.from && // Adjacent or overlapping
            last.maxCurrent === schedule.maxCurrent && // Same current
            schedule.from - last.from <= SECONDS_IN_A_DAY // Within 24 hours
        ) {
            const potentialNewTo = Math.max(last.to, schedule.to)
            if (potentialNewTo - last.from <= SECONDS_IN_A_DAY) {
                last.to = potentialNewTo // Merge schedules
            } else {
                // Cannot merge as it exceeds 24 hours
                merged.push({ ...schedule })
            }
        } else {
            merged.push({ ...schedule })
        }
    }

    return merged
}
